import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { renewSession } from './authRefresh';
import { getLogoutNotice, markUserActivity } from '../../utility/auth/sessionSync';
import { traceCrash } from '../../utility/debug/crashTrace';
import { isAuthRedirectInProgress, isPublicAuthRoute } from '../../utility/auth/authRedirect';

const rawBaseQuery = fetchBaseQuery({
  credentials: 'include',
});

const AUTH_ENDPOINTS_WITHOUT_RETRY = new Set([
  '/api/auth/token',
  '/api/auth/token:azure',
  '/api/auth/verifyTwoFactor',
  '/api/auth/refresh',
  '/api/auth/logout',
]);

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const getRequestUrl = (args: string | FetchArgs) => (typeof args === 'string' ? args : args.url);

const getRequestMethod = (args: string | FetchArgs) => {
  if (typeof args === 'string') {
    return 'GET';
  }

  return args.method?.toUpperCase() ?? 'GET';
};

const canRetryWithRefresh = (args: string | FetchArgs) => {
  return !AUTH_ENDPOINTS_WITHOUT_RETRY.has(getRequestUrl(args));
};

const canAttemptRefresh = () => {
  return !getLogoutNotice() && !isPublicAuthRoute() && !isAuthRedirectInProgress();
};

const isWriteRequest = (args: string | FetchArgs) => {
  return WRITE_METHODS.has(getRequestMethod(args));
};

const shouldTraceRequest = (args: string | FetchArgs) => {
  const url = getRequestUrl(args);

  return (
    url.includes('/api/salemovements') ||
    url.includes('/api/auth/refresh') ||
    url.includes('/api/operations')
  );
};

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const trace = shouldTraceRequest(args);
  const requestUrl = getRequestUrl(args);
  const requestMethod = getRequestMethod(args);

  if (trace) {
    traceCrash('baseQuery.start', {
      url: requestUrl,
      method: requestMethod,
    });
  }

  if (isWriteRequest(args) && !AUTH_ENDPOINTS_WITHOUT_RETRY.has(requestUrl) && !getLogoutNotice()) {
    markUserActivity();

    if (trace) {
      traceCrash('baseQuery.markUserActivity.write', {
        url: requestUrl,
        method: requestMethod,
      });
    }
  }

  let result = await rawBaseQuery(args, api, extraOptions);

  if (trace) {
    traceCrash('baseQuery.result', {
      url: requestUrl,
      method: requestMethod,
      hasError: Boolean(result.error),
      status: result.error?.status ?? 'ok',
    });
  }

  if (result.error?.status === 401 && canRetryWithRefresh(args) && canAttemptRefresh()) {
    traceCrash('baseQuery.401.before-refresh', {
      url: requestUrl,
      method: requestMethod,
    });

    const refreshed = await renewSession();

    traceCrash('baseQuery.401.after-refresh', {
      isAuthenticated: Boolean(refreshed?.isAuthenticated),
    });

    if (refreshed?.isAuthenticated) {
      markUserActivity();
      result = await rawBaseQuery(args, api, extraOptions);

      traceCrash('baseQuery.retry.result', {
        url: requestUrl,
        method: requestMethod,
        hasError: Boolean(result.error),
        status: result.error?.status ?? 'ok',
      });
    }
  }

  return result;
};
